import { useContext, useMemo, useState } from 'react'
import { DropdownMenu } from 'radix-ui'
import { cn } from '@/utlis/cn'
import { SidebarTooltip as Tooltip } from '@/pages/SessionPage/internal/sidebar/SidebarTooltip'
import { PortalContext } from '../PortalContext'
import { isElectron } from '@/utlis/isElectron'
import { title_bar_height_px } from '@/services/Constants'
import { Button } from '../Button'
import CaretLeftIcon from '@/CustomIcons/CaretLeftIcon'
import '@/components/dropdown-animation.css'

type MenuButtonProps = {
    icon?: React.ReactNode
    content?: React.ReactNode
    contentClassName?: string
    disabled?: boolean
    tooltip?: React.ComponentProps<typeof Tooltip>
    label?: string | null
    hideArrow?: boolean
    placement?: 'bottom' | 'left'
    innerButtonClassName?: string
    triggerClassName?: string
    zIndex?: number
    labelPosition?: 'inside' | 'outside'
    noArrowIcon?: boolean
    menuButtonClassesOverride?: string
    customButton?: React.ReactNode
    separator?: boolean
    onIconClick?: () => void
    onOpenChange?: (open: boolean) => void
}

const menuButtonBaseStyles: string[] = [
    'w-full h-full',
    'min-w-[55px] md:min-w-[68px]',
    'flex',
    'items-center',
    'justify-center',
    'overflow-hidden',
    'rounded-[20px]',
    'bg-[rgba(243,243,243,0.05)]',
    'border-muse-toplit-low',
]

//Inner and outer content styles are so the scrollbar (if there is one) doesn't hang over the corners
const outerContentStyles = [
    'overflow-hidden',
    'p-[10px]',
    'rounded-[20px]',
    'bg-[rgba(58,58,58,1)]',
    'border border-white/5',
    'shadow-glow',
    'bg-blend-luminosity',
    'inline-flex',
    'flex-col',
]

const innerContentStyles = [
    'inline-flex',
    'flex-col',
    'gap-[10px]',
    'items-center',
    'p-[10px]',
    'overflow-y-auto',
    'h-full',
    'max-w-[88vw]',
]

export default function MenuButton({
    placement,
    icon,
    disabled,
    tooltip,
    content,
    contentClassName,
    label,
    triggerClassName,
    innerButtonClassName,
    zIndex = 30,
    labelPosition = 'outside',
    noArrowIcon = false,
    menuButtonClassesOverride = '',
    customButton,
    separator,
    onIconClick,
    onOpenChange,
}: MenuButtonProps) {
    const [isOpen, setIsOpen] = useState(false)
    const portalContainer = useContext(PortalContext)

    const menuButtonClasses = menuButtonClassesOverride
        ? menuButtonClassesOverride
        : cn(menuButtonBaseStyles, disabled ? 'opacity-50' : 'cursor-pointer')

    const contentMaxHeight = isElectron()
        ? { maxHeight: `calc(var(--radix-dropdown-menu-content-available-height) - ${title_bar_height_px}px)` }
        : { maxHeight: 'var(--radix-dropdown-menu-content-available-height)' }

    const popoverIcon = useMemo(() => {
        if (noArrowIcon) {
            return null
        }
        const up = placement === 'bottom' ? !isOpen : isOpen
        return (
            <div className={cn('transition-transform', up ? '-rotate-90' : 'rotate-90')}>
                <CaretLeftIcon />
            </div>
        )
    }, [isOpen, placement])

    const innerButtonClasses = cn(
        'w-full h-full flex items-center justify-center',
        !disabled &&
            'hover:bg-[rgba(255,255,255,0.15)] active:bg-[rgba(255,255,255,0.08)] active:border-white/15 cursor-pointer'
    )

    const renderPopoverButton = () => {
        if (customButton) {
            return <DropdownMenu.Trigger asChild>{customButton}</DropdownMenu.Trigger>
        }

        const innerLabel = label && labelPosition === 'inside' && <span className="text-nowrap">{label}</span>

        /* split button */
        if (content && onIconClick) {
            return (
                <div className={cn(menuButtonClasses, 'grid grid-cols-2', triggerClassName)}>
                    <Tooltip {...tooltip}>
                        <div className="h-full">
                            <button
                                onClick={onIconClick}
                                className={cn(
                                    'px-[5px] md:px-[10px] -outline-offset-[10px]',
                                    innerButtonClasses,
                                    innerButtonClassName
                                )}
                            >
                                {icon}
                            </button>
                            {innerLabel}
                        </div>
                    </Tooltip>
                    <DropdownMenu.Trigger
                        className={cn(
                            'px-[3px] md:px-[5px] focus-visible:outline-none',
                            innerButtonClasses,
                            separator && 'border-l-1 border-white/5',
                            innerButtonClassName
                        )}
                        disabled={disabled}
                    >
                        <div>{popoverIcon}</div>
                    </DropdownMenu.Trigger>
                </div>
            )
        }

        /* Normal menu button */
        if (content) {
            return (
                <Tooltip {...tooltip}>
                    <DropdownMenu.Trigger
                        disabled={disabled}
                        className={cn(
                            menuButtonClasses,
                            'flex gap-[5px] md:gap-[10px] px-[8px] md:px-[15px]',
                            innerButtonClasses,
                            innerLabel && 'justify-between',
                            triggerClassName,
                            innerButtonClassName
                        )}
                    >
                        {innerLabel ? (
                            <>
                                <div className="flex gap-2">
                                    {icon}
                                    {innerLabel}
                                </div>
                                {popoverIcon}
                            </>
                        ) : (
                            <>
                                {icon}
                                <div className="menu-button-indicator-arrow">{popoverIcon}</div>
                            </>
                        )}
                    </DropdownMenu.Trigger>
                </Tooltip>
            )
        }

        /* Non-menu button */
        return (
            <Tooltip {...tooltip}>
                <Button
                    onClick={onIconClick}
                    className={cn(
                        menuButtonClasses,
                        'px-[5px] md:px-[10px] gap-2',
                        innerButtonClasses,
                        innerLabel && 'justify-start',
                        triggerClassName,
                        innerButtonClassName
                    )}
                >
                    {icon}
                    {innerLabel}
                </Button>
            </Tooltip>
        )
    }

    return (
        <div className="flex flex-col items-center gap-[5px]">
            <DropdownMenu.Root
                onOpenChange={(open) => {
                    setIsOpen(open)
                    onOpenChange?.(open)
                }}
                modal={false}
            >
                {renderPopoverButton()}
                <DropdownMenu.Portal container={portalContainer}>
                    <DropdownMenu.Content
                        side={placement}
                        sideOffset={4}
                        className={cn('dropdown-menu-content', outerContentStyles)}
                        style={{ zIndex, ...contentMaxHeight }}
                        collisionPadding={{ top: 28, right: 8, left: 8, bottom: 8 }}
                    >
                        <div className={cn(innerContentStyles, contentClassName)}>{content}</div>
                    </DropdownMenu.Content>
                </DropdownMenu.Portal>
            </DropdownMenu.Root>
            {label && labelPosition === 'outside' && (
                <span className="menu-button-label t-sm-light opacity-50 text-nowrap">{label}</span>
            )}
        </div>
    )
}

type MenuItemProps = {
    children?: React.ReactNode
    asChild?: boolean
    className?: string
}
export function MenuItem(props: MenuItemProps) {
    return (
        <DropdownMenu.Item
            className={props.className}
            asChild={props.asChild}
            // Radix ui by default focuses the item when you hover over it, which causes our submenus to close. This prevents that.
            // see https://github.com/radix-ui/primitives/issues/2193
            onPointerMove={(e) => e.preventDefault()}
            onPointerLeave={(e) => e.preventDefault()}
        >
            {props.children}
        </DropdownMenu.Item>
    )
}
